import {
  AssignmentTurnedIn,
  ContentCopy,
  Delete,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  Collapse,
  Fab,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import {
  DateInput,
  DecimalInput,
  MoneyInput,
  TextInput,
} from 'lib/common/Inputs';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { isDateAWeekDay } from 'lib/functions/dates';
import { formatDate, formatMoney } from 'lib/functions/utils';
import React, { useState } from 'react';

import InputWithBadge from '../InputWithBadge/InputWithBadge';

const useStyles = makeStyles(() =>
  createStyles({
    table: {
      width: '100%',
      minWidth: '1200px',
      borderCollapse: 'separate',
      borderSpacing: 0,
    },
    tableCell: {
      padding: '12px 8px !important',
      overflow: 'hidden',
      minWidth: '80px',
      maxWidth: '150px',
      whiteSpace: 'nowrap',
      fontSize: '13px !important',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
    tableCellFolio: {
      padding: '12px 4px !important',
      overflow: 'hidden',
      minWidth: '60px',
      maxWidth: '80px',
      whiteSpace: 'nowrap',
      fontSize: '13px !important',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
    tableCellCost: {
      padding: '12px 4px !important',
      overflow: 'hidden',
      minWidth: '80px',
      maxWidth: '100px',
      whiteSpace: 'nowrap',
      fontSize: '13px !important',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
    tableCellTasa: {
      padding: '12px 8px !important',
      overflow: 'hidden',
      minWidth: '80px',
      maxWidth: '100px',
      whiteSpace: 'nowrap',
      fontSize: '13px !important',
      textAlign: 'right',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
    tableCellNames: {
      padding: '12px 8px !important',
      overflow: 'hidden',
      minWidth: '120px',
      maxWidth: '160px',
      whiteSpace: 'nowrap',
      fontSize: '13px !important',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
    tableContainer: {
      border: '1px solid #eceff3',
      borderRadius: '12px',
      overflow: 'auto',
      maxHeight: '600px',
      overflowY: 'auto',
      overflowX: 'auto',
      width: '100%',
    },
    tableBody: {
      backgroundColor: '#ffffff',
    },
    tableHead: {
      backgroundColor: '#f8f9fa !important',
      '& th': {
        backgroundColor: '#f8f9fa !important',
        color: '#495057 !important',
        fontWeight: '600 !important',
        fontSize: '13px !important',
        padding: '16px 8px !important',
        borderBottom: '2px solid #dee2e6',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      },
    },
    textField: {
      fontSize: '13px !important',
      '& .MuiOutlinedInput-root': {
        borderRadius: '6px',
        '& fieldset': {
          borderColor: '#e0e0e0',
        },
        '&:hover fieldset': {
          borderColor: '#1976d2',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#1976d2',
        },
      },
      '& .MuiInputBase-input': {
        padding: '8px 12px',
        fontSize: '13px',
      },
    },
    iconButton: {
      padding: '4px',
      margin: '0 1px',
      borderRadius: '4px',
      '&:hover': {
        backgroundColor: 'rgba(25, 118, 210, 0.08)',
      },
    },
    deleteButton: {
      color: '#dc3545',
      '&:hover': {
        backgroundColor: 'rgba(220, 53, 69, 0.08)',
      },
    },
    assignButton: {
      color: '#28a745',
      '&:hover': {
        backgroundColor: 'rgba(40, 167, 69, 0.08)',
      },
    },
    tableRow: {
      '&:hover': {
        backgroundColor: '#f8f9fa',
        transition: 'background-color 0.2s ease',
      },
      '&:nth-of-type(even)': {
        backgroundColor: '#fafbfc',
      },
      '&:nth-of-type(even):hover': {
        backgroundColor: '#f1f3f4',
      },
    },
    amountCell: {
      fontWeight: '600',
      color: '#2c3e50',
    },
    daysCell: {
      fontWeight: '500',
      color: '#6c757d',
    },
    rateCostCell: {
      fontWeight: '600',
      color: '#28a745',
    },
    actionsCell: {
      minWidth: '60px',
      maxWidth: '80px',
      padding: '6px !important',
    },
    chip: {
      fontSize: '11px',
      height: '20px',
      borderRadius: '10px',
    },
    '@media (max-width: 1200px)': {
      tableCell: {
        fontSize: '12px !important',
        padding: '8px 4px !important',
        maxWidth: '150px',
      },
      tableCellFolio: {
        fontSize: '12px !important',
        padding: '6px 2px !important',
        maxWidth: '60px',
      },
      tableCellNames: {
        fontSize: '12px !important',
        padding: '8px 4px !important',
        maxWidth: '100px',
      },
      tableCellTasa: {
        fontSize: '12px !important',
        padding: '8px 4px !important',
        maxWidth: '60px',
      },
      tableCellCost: {
        fontSize: '12px !important',
        padding: '6px 2px !important',
        maxWidth: '80px',
      },
    },
  }),
);

import type { ExtensionDocument } from '../../../../types';

interface ExtensionsTableProps {
  extensions: ExtensionDocument[];
  setExtensions: (extensions: ExtensionDocument[]) => void;
  fixedArrearRate: number | null;
  noIvaComision: number | null;
  canChangeArrearRate: boolean;
}

const ExtensionsTable = (props: ExtensionsTableProps) => {
  const {
    extensions,
    setExtensions,
    fixedArrearRate,
    noIvaComision,
    canChangeArrearRate,
  } = props;
  const classes = useStyles();
  const [expandedRows, setExpandedRows] = useState<number[]>([0]);

  React.useEffect(() => {
    if (fixedArrearRate !== null && extensions.length > 0) {
      const newExtensions = extensions.map((doc: any) => {
        const days =
          typeof doc.days === 'number' && !Number.isNaN(doc.days)
            ? doc.days
            : calculateDays(doc.paymentDate, doc.dueDate);
        const safeDays = days > 0 ? days : 0;
        const rateCost = calculateRateCost(
          doc.documentRemainingAmountNet,
          fixedArrearRate,
          safeDays,
        );
        return {
          ...doc,
          rate: fixedArrearRate.toString(),
          rateCost,
        };
      });
      setExtensions(newExtensions);
    }
  }, [fixedArrearRate]);

  const toggleRowExpansion = (rowIndex: number) => {
    setExpandedRows((prev) =>
      prev.includes(rowIndex)
        ? prev.filter((index) => index !== rowIndex)
        : [...prev, rowIndex],
    );
  };

  const handleChangeObservation = (value: string, rowIndex: number) => {
    const newExtensions = extensions.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return {
          ...doc,
          observation: value,
        };
      }
      return doc;
    });
    setExtensions(newExtensions);
  };

  const copyFirstObservationToAll = () => {
    if (extensions.length === 0) return;

    const firstObservation = extensions[0].observation || '';
    const newExtensions = extensions.map((doc: any, i: any) => {
      if (i === 0) {
        return doc;
      }
      return {
        ...doc,
        observation: firstObservation,
      };
    });
    setExtensions(newExtensions);
  };

  const calculateDays = (start: string, end: string) => {
    const startDate = new Date(start.split('T')[0]);
    const endDate = new Date(end);
    return Math.floor(
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
    );
  };

  const calculateRateCost = (amount: number, rate: number, days: number) => {
    return Math.round((amount * rate * days) / 30 / 100);
  };

  const dateValidators = (date: string, rowIndex: number) => {
    if (!date) return false;

    if (!isDateAWeekDay(new Date(date))) {
      alert('La fecha de vencimiento debe ser un día hábil');
      return false;
    }

    const days = calculateDays(extensions[rowIndex].paymentDate, date);

    if (days <= 0) {
      alert(
        'La fecha de vencimiento no puede ser menor a la fecha de liberación',
      );
      return false;
    }

    return days;
  };

  const handleChangeDate = (value: string, rowIndex: number) => {
    if (!value) {
      const newExtensions = extensions.map((doc: any, i: any) => {
        if (i === rowIndex) {
          return { ...doc, dueDate: '', days: 0, rateCost: 0 };
        }
        return doc;
      });
      setExtensions(newExtensions);
      return;
    }

    const days = dateValidators(value, rowIndex);
    if (!days) return;

    const newExtensions = extensions.map((doc: any, i: any) => {
      if (i === rowIndex) {
        const rateNumber = parseFloat(doc.rate || '0');
        const rateCost = calculateRateCost(
          doc.documentRemainingAmountNet,
          Number.isNaN(rateNumber) ? 0 : rateNumber,
          days,
        );
        return {
          ...doc,
          dueDate: value,
          days,
          rateCost,
        };
      }
      return doc;
    });
    setExtensions(newExtensions);
  };

  const handleChangeRate = (value: number | null, rowIndex: number) => {
    if (fixedArrearRate !== null) return;

    const nextRate = value ?? 0;
    const newExtensions = extensions.map((doc: any, i: any) => {
      if (i !== rowIndex) return doc;

      const rateCost = calculateRateCost(
        doc.documentRemainingAmountNet,
        nextRate,
        doc.days,
      );

      return {
        ...doc,
        rate: String(nextRate),
        rateCost,
      };
    });

    setExtensions(newExtensions);
  };

  const handleChangeOperationCostFixed = (
    value: number | null,
    rowIndex: number,
  ) => {
    const nextValue = value ?? 0;
    const newExtensions = extensions.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return { ...doc, operationCostFixed: nextValue };
      }
      return doc;
    });
    setExtensions(newExtensions);
  };

  const handleChangeOperationCostVariable = (
    value: number | null,
    rowIndex: number,
  ) => {
    const nextRate = value ?? null;
    const newExtensions = extensions.map((doc: any, i: any) => {
      if (i !== rowIndex) return doc;

      if (nextRate === null) {
        return {
          ...doc,
          operationCostRateVariable: '',
          operationCostVariable: 0,
        };
      }

      return {
        ...doc,
        operationCostRateVariable: String(nextRate),
        operationCostVariable:
          (doc.documentRemainingAmountNet * nextRate) / 100,
      };
    });

    setExtensions(newExtensions);
  };

  const assignConditionsForAllExtensions = () => {
    if (extensions.length === 0) return;

    const newExtensions = extensions.map((doc: any, i: any) => {
      if (i === 0) {
        return doc;
      }

      const firstRow = extensions[0];
      const rate = parseFloat(firstRow.rate);
      const operationCostFixed = Number(firstRow.operationCostFixed);
      const operationCostRateVariable = parseFloat(
        firstRow.operationCostRateVariable,
      );
      const dueDate = firstRow.dueDate;

      const days = calculateDays(doc.paymentDate, dueDate);
      const safeDays = days > 0 ? days : 0;

      const rateCost = calculateRateCost(
        doc.documentRemainingAmountNet,
        rate,
        safeDays,
      );

      const operationCostVariable =
        operationCostRateVariable > 0
          ? (doc.documentRemainingAmountNet * operationCostRateVariable) / 100
          : 0;

      return {
        ...doc,
        rate: firstRow.rate,
        rateCost,
        dueDate,
        operationCostFixed,
        operationCostVariable,
        operationCostRateVariable: firstRow.operationCostRateVariable,
        days: safeDays,
      };
    });
    setExtensions([
      ...newExtensions.map((extension: any, index: any) => ({
        ...extension,
        key: index,
      })),
    ]);
  };

  const assignRateToAllExtensions = () => {
    if (extensions.length === 0) return;

    const rateToApply =
      fixedArrearRate !== null
        ? fixedArrearRate.toString()
        : extensions[0].rate;

    const newExtensions = extensions.map((doc: any, i: any) => {
      if (i === 0) {
        return doc;
      }
      const safeDays = doc.days > 0 ? doc.days : 0;
      const rateCost = calculateRateCost(
        doc.documentRemainingAmountNet,
        parseFloat(rateToApply),
        safeDays,
      );
      return {
        ...doc,
        rate: rateToApply,
        rateCost,
      };
    });

    setExtensions([
      ...newExtensions.map((extension: any, index: any) => ({
        ...extension,
        key: index,
      })),
    ]);
  };

  const deleteRow = (rowIndex: number) => {
    const newExtensions = extensions.filter(
      (doc: any, i: any) => i !== rowIndex,
    );
    setExtensions(newExtensions);
  };

  return (
    <TableContainer className={classes.tableContainer}>
      <Table className={classes.table}>
        <TableHead className={classes.tableHead}>
          <TableRow>
            <TableCell className={classes.actionsCell}></TableCell>
            <TableCell className={classes.tableCellFolio}>Folio</TableCell>
            <TableCell className={classes.tableCellNames}>
              Deudor / Cliente
            </TableCell>
            <TableCell className={classes.tableCell}>Saldos</TableCell>
            <TableCell className={classes.tableCell}>Liberación</TableCell>
            <TableCell className={classes.tableCellTasa}>
              <LightTooltip
                placement="top"
                title={
                  <Typography style={{ padding: 8 }}>
                    {fixedArrearRate !== null
                      ? `Tasa fija configurada: ${fixedArrearRate}%`
                      : 'Hacer clic para asignar la tasa del primer elemento a todos'}
                  </Typography>
                }
              >
                <Box
                  onClick={assignRateToAllExtensions}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    '&:hover': {
                      color: '#1976d2',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Tasa (%)
                </Box>
              </LightTooltip>
            </TableCell>
            <TableCell className={classes.tableCellCost}>Costo fijo</TableCell>
            <TableCell className={classes.tableCellCost}>
              Costo var. (%)
            </TableCell>
            <TableCell className={classes.tableCell}>
              Fecha vencimiento
            </TableCell>
            <TableCell className={classes.tableCell}>Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody className={classes.tableBody}>
          {extensions.map((row: any, rowIndex: number) => (
            <React.Fragment key={rowIndex}>
              <TableRow className={classes.tableRow}>
                <TableCell className={classes.actionsCell}>
                  <Box display="flex" justifyContent="start">
                    <IconButton
                      className={classes.iconButton}
                      onClick={() => toggleRowExpansion(rowIndex)}
                      size="small"
                    >
                      {expandedRows.includes(rowIndex) ? (
                        <ExpandLess fontSize="small" />
                      ) : (
                        <ExpandMore fontSize="small" />
                      )}
                    </IconButton>
                    <IconButton
                      className={classes.iconButton}
                      onClick={() => deleteRow(rowIndex)}
                      size="small"
                    >
                      <Delete
                        className={classes.deleteButton}
                        fontSize="small"
                      />
                    </IconButton>
                    {rowIndex === 0 && (
                      <LightTooltip
                        placement="right"
                        title={
                          <Typography style={{ padding: 12 }}>
                            Asignar mismas condiciones a todas
                          </Typography>
                        }
                      >
                        <IconButton
                          className={classes.iconButton}
                          onClick={() => assignConditionsForAllExtensions()}
                          size="small"
                        >
                          <AssignmentTurnedIn
                            className={classes.assignButton}
                            fontSize="small"
                          />
                        </IconButton>
                      </LightTooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell className={classes.tableCellFolio}>
                  {row.folio}
                </TableCell>
                <TableCell className={classes.tableCellNames}>
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                      }}
                    >
                      {row.stakeholderName}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                      }}
                    >
                      {row.businessName}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Stack spacing={0.5} alignItems="center">
                    <Box className={classes.amountCell} textAlign="center">
                      <Chip
                        label={`Monto: ${formatMoney(row.amount)}`}
                        className={classes.chip}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{ fontSize: '10px', height: '18px' }}
                      />
                    </Box>
                    <Box className={classes.amountCell} textAlign="center">
                      <Chip
                        label={`Deuda: ${formatMoney(row.documentRemainingAmountNet)}`}
                        className={classes.chip}
                        color="error"
                        variant="outlined"
                        size="small"
                        sx={{ fontSize: '10px', height: '18px' }}
                      />
                    </Box>

                    <Box className={classes.amountCell} textAlign="center">
                      <Chip
                        label={`Atraso: ${formatMoney(row.totalArrear)}`}
                        className={classes.chip}
                        color="warning"
                        variant="outlined"
                        size="small"
                        sx={{ fontSize: '10px', height: '18px' }}
                      />
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {formatDate(row.paymentDate, 'dd-mm-yyyy')}
                </TableCell>
                <TableCell className={classes.tableCellTasa}>
                  <InputWithBadge badge={formatMoney(row.rateCost)}>
                    <DecimalInput
                      value={
                        fixedArrearRate !== null
                          ? fixedArrearRate
                          : row.rate
                            ? Number(row.rate)
                            : null
                      }
                      onChange={(v) => handleChangeRate(v, rowIndex)}
                      isPercentage
                      disabled={
                        !canChangeArrearRate || fixedArrearRate !== null
                      }
                      fullWidth
                    />
                  </InputWithBadge>
                </TableCell>
                <TableCell className={classes.tableCellCost}>
                  <InputWithBadge
                    badge={formatMoney(
                      row.operationCostFixed * (noIvaComision ? 1 : 1.19),
                    )}
                  >
                    <MoneyInput
                      value={row.operationCostFixed}
                      onChange={(v) =>
                        handleChangeOperationCostFixed(v, rowIndex)
                      }
                      currency="CLP"
                      fullWidth
                    />
                  </InputWithBadge>
                </TableCell>
                <TableCell className={classes.tableCellCost}>
                  <InputWithBadge
                    badge={formatMoney(
                      row.operationCostVariable * (noIvaComision ? 1 : 1.19),
                    )}
                  >
                    <DecimalInput
                      value={
                        row.operationCostRateVariable
                          ? Number(row.operationCostRateVariable)
                          : null
                      }
                      onChange={(v) =>
                        handleChangeOperationCostVariable(v, rowIndex)
                      }
                      isPercentage
                      fullWidth
                    />
                  </InputWithBadge>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <InputWithBadge
                    badge={`${row.days || 0} días`}
                    badgeSx={{ right: '30px' }}
                  >
                    <DateInput
                      value={row.dueDate}
                      onChange={(value) => handleChangeDate(value, rowIndex)}
                      fullWidth
                    />
                  </InputWithBadge>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {formatMoney(
                    (row.operationCostVariable * (noIvaComision ? 1 : 1.19) ||
                      0) +
                      (row.operationCostFixed * (noIvaComision ? 1 : 1.19) ||
                        0) +
                      (row.rateCost || 0),
                  )}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell
                  style={{ paddingBottom: 0, paddingTop: 0 }}
                  colSpan={10}
                >
                  <Collapse
                    in={expandedRows.includes(rowIndex)}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box sx={{ margin: 1, position: 'relative' }}>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        margin="8px 0px"
                      >
                        Observaciones
                      </Typography>
                      <TextInput
                        multiline
                        rows={3}
                        value={row.observation || ''}
                        onChange={(v) =>
                          handleChangeObservation(v ?? '', rowIndex)
                        }
                        placeholder="Ingresar observaciones para esta prórroga..."
                        fullWidth
                      />
                      {rowIndex === 0 && extensions.length > 1 && (
                        <LightTooltip
                          placement="top"
                          title={
                            <Typography style={{ padding: 8 }}>
                              Copiar esta observación a todas las demás
                              prórrogas
                            </Typography>
                          }
                        >
                          <Fab
                            size="small"
                            color="primary"
                            onClick={copyFirstObservationToAll}
                            sx={{
                              position: 'absolute',
                              bottom: 6,
                              right: 6,
                              width: 24,
                              height: 24,
                              minWidth: 24,
                              minHeight: 24,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              '&:hover': {
                                boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                                transform: 'scale(1.05)',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <ContentCopy sx={{ fontSize: '14px' }} />
                          </Fab>
                        </LightTooltip>
                      )}
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ExtensionsTable;
